/**
 * Sube un blob a una URL prefirmada, con progreso y reintentos.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ¿Por qué XMLHttpRequest y no fetch?
 * ─────────────────────────────────────────────────────────────────────────────
 * `fetch` NO expone progreso de subida. Se puede leer el progreso de descarga
 * con un ReadableStream, pero el de subida no está disponible en ningún
 * navegador estable. Con subidas que en Perú pueden durar horas, una barra de
 * progreso no es un adorno: es lo que distingue "está funcionando" de "se ha
 * colgado" y evita que el usuario recargue y lo pierda todo.
 *
 * `XMLHttpRequest.upload.onprogress` sí lo da. Es la razón, y la única, de usar
 * una API antigua aquí.
 */

export interface PutOptions {
  signal?: AbortSignal
  onProgress?: (bytesSent: number, bytesTotal: number) => void
  /** Se invoca al recibir 403: la URL expiró y hay que pedir una nueva. */
  onExpired?: () => Promise<string>
  headers?: Record<string, string>
  retries?: number
  /**
   * Sólo el multipart necesita el ETag: es lo que se envía a
   * CompleteMultipartUpload. En un PUT simple el backend obtiene el ETag
   * definitivo con HeadObject, así que no leerlo no rompe nada — y evita
   * fallar la subida por una política CORS incompleta.
   */
  requireEtag?: boolean
}

export class UploadError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly retryable: boolean,
  ) {
    super(message)
    this.name = 'UploadError'
  }
}

export async function putWithProgress(
  url: string,
  body: Blob,
  options: PutOptions = {},
): Promise<string> {
  const maxAttempts = (options.retries ?? 5) + 1
  let currentUrl = url

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await singlePut(currentUrl, body, options)
    } catch (error) {
      const isLast = attempt === maxAttempts

      if (!(error instanceof UploadError)) throw error
      if (error.status === 0 && options.signal?.aborted) throw error

      // 403 sobre una URL prefirmada casi siempre significa expiración, no
      // falta de permisos: el backend firmó con TTL y la subida se alargó.
      // Pedimos firma nueva y NO lo contamos como reintento perdido.
      if (error.status === 403 && options.onExpired) {
        currentUrl = await options.onExpired()
        continue
      }

      if (!error.retryable || isLast) throw error

      // Backoff exponencial con jitter. El jitter importa: sin él, 16 partes
      // que fallan a la vez por un corte de red reintentan a la vez y vuelven
      // a tumbar la conexión.
      const base = Math.min(1000 * 2 ** (attempt - 1), 30_000)
      const delay = base * (0.5 + Math.random() * 0.5)
      await sleep(delay, options.signal)
    }
  }

  throw new UploadError('Se agotaron los reintentos.', 0, false)
}

function singlePut(url: string, body: Blob, options: PutOptions): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url, true)

    for (const [name, value] of Object.entries(options.headers ?? {})) {
      xhr.setRequestHeader(name, value)
    }

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) options.onProgress?.(event.loaded, event.total)
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        // El ETag es OBLIGATORIO para completar un multipart. Sólo es legible
        // si el bucket expone la cabecera: ExposeHeaders: ["ETag"] en la
        // política CORS. Sin eso, esto devuelve null y la subida no se puede
        // cerrar (PLAN.md §10.1).
        const etag = xhr.getResponseHeader('ETag')

        if (etag === null && options.requireEtag === true) {
          reject(
            new UploadError(
              'El almacenamiento no expuso la cabecera ETag, así que la subida ' +
                'multiparte no se puede cerrar. Revisa ExposeHeaders en la política ' +
                'CORS del bucket.',
              xhr.status,
              false,
            ),
          )
          return
        }

        resolve(etag?.replaceAll('"', '') ?? '')
        return
      }

      reject(
        new UploadError(
          `La subida falló con ${xhr.status}`,
          xhr.status,
          // 5xx y 429 son transitorios; 4xx (salvo 429) indican un problema
          // real que no se arregla reintentando.
          xhr.status >= 500 || xhr.status === 429,
        ),
      )
    }

    xhr.onerror = () => reject(new UploadError('Error de red.', 0, true))
    xhr.ontimeout = () => reject(new UploadError('Tiempo de espera agotado.', 0, true))
    xhr.onabort = () => reject(new UploadError('Subida cancelada.', 0, false))

    options.signal?.addEventListener('abort', () => xhr.abort(), { once: true })

    xhr.send(body)
  })
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms)
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(timer)
        reject(new UploadError('Subida cancelada.', 0, false))
      },
      { once: true },
    )
  })
}
